import React from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  CircularProgress,
  Chip,
  alpha,
  useTheme,
} from '@mui/material';
import MiscellaneousServicesIcon from '@mui/icons-material/MiscellaneousServices';
import { PsiService, PsiProfileDTO } from '@inversor-hub/types';
import { psiServiceLabels } from '@/features/psi/constants';
import { formatCurrency, formatPercentage } from '@/utils/misc';

const getPercentageReferenceLabel = (service: PsiService): string => {
  const labels: Record<PsiService, string> = {
    OPPORTUNITY_SEARCH: 'del precio de compra',
    REFURBISHMENT: 'del coste de reforma',
    TENANT_SEARCH: 'del alquiler anual',
    RENTAL_MANAGEMENT: 'del alquiler anual',
  };
  return labels[service] || '';
};

interface PsiServiceSelectionProps {
  psiProfile: PsiProfileDTO | undefined;
  isLoading: boolean;
  selectedServices: PsiService[];
  onServiceToggle: (service: PsiService) => void;
  disabled?: boolean;
  showError?: boolean;
}

/**
 * Reusable component for selecting PSI services.
 * Displays available services with pricing information and handles selection state.
 */
export const PsiServiceSelection: React.FC<PsiServiceSelectionProps> = ({
  psiProfile,
  isLoading,
  selectedServices,
  onServiceToggle,
  disabled = false,
  showError = false,
}) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        bgcolor: alpha(theme.palette.info.main, 0.04),
        border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
        borderRadius: 2,
      }}
    >
      <Stack spacing={2}>
        {/* Header Section */}
        <Box>
          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
            <MiscellaneousServicesIcon color="info" />
            <Typography variant="subtitle1" fontWeight={600}>
              Servicios del PSI
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Selecciona los servicios que deseas que el PSI te proporcione para esta operación
          </Typography>
        </Box>

        {/* Loading State */}
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={24} />
          </Box>
        ) : psiProfile ? (
          <>
            {/* Package Pricing Info */}
            {psiProfile.pricingModel === 'PACKAGE' && (
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: alpha(theme.palette.success.main, 0.08),
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2" fontWeight={600} color="success.dark">
                  Tarifa paquete: {formatCurrency(psiProfile.feeAmount, 0)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Esta tarifa incluye todos los servicios seleccionados
                </Typography>
              </Box>
            )}

            {/* Service Selection */}
            <FormGroup>
              {psiProfile.serviceConfigs.map((serviceConfig) => {
                const isMandatory = serviceConfig.service === 'OPPORTUNITY_SEARCH';
                return (
                  <FormControlLabel
                    key={serviceConfig.service}
                    control={
                      <Checkbox
                        checked={selectedServices.includes(serviceConfig.service) || isMandatory}
                        onChange={
                          isMandatory ? undefined : () => onServiceToggle(serviceConfig.service)
                        }
                        disabled={disabled || isMandatory}
                      />
                    }
                    label={
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Typography variant="body2">
                          {psiServiceLabels[serviceConfig.service]}
                        </Typography>
                        {psiProfile.pricingModel === 'PER_SERVICE' && (
                          <Chip
                            label={
                              serviceConfig.feeType === 'FIXED' && serviceConfig.fee
                                ? formatCurrency(serviceConfig.fee, 0)
                                : serviceConfig.feeType === 'PERCENTAGE' && serviceConfig.percentage
                                  ? `${formatPercentage(serviceConfig.percentage)} ${getPercentageReferenceLabel(serviceConfig.service)}`
                                  : 'No configurado'
                            }
                            size="small"
                            color={
                              serviceConfig.fee || serviceConfig.percentage ? 'success' : 'default'
                            }
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              height: 20,
                            }}
                          />
                        )}
                      </Box>
                    }
                    sx={{ mb: 0.5 }}
                  />
                );
              })}
            </FormGroup>

            {/* Validation Error */}
            {showError && selectedServices.length === 0 && (
              <FormHelperText error sx={{ ml: 0 }}>
                Debes seleccionar al menos un servicio
              </FormHelperText>
            )}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No se pudieron cargar los servicios del PSI
          </Typography>
        )}
      </Stack>
    </Paper>
  );
};
